import {
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import clsx from "classnames";
import { useStyles } from "./TextField.style";
import { Typography } from "../Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import CloseIcon from "../../../assets/icons/close@1x.webp";
import CloseIcon2x from "../../../assets/icons/close@2x.webp";
import CloseIcon3x from "../../../assets/icons/close@3x.webp";
import { StaticImage } from "../StaticImage/StaticImage";
import { Dialog } from "../Dialog/Dialog";
import { isTV, isIOS } from "../../../utils/platform";
import { Icon } from "../Icon/Icon";
import { TVKeyboard } from "../../common/TVKeyboard/TVKeyboard";
import { useTranslation } from "react-i18next";

let hasKeyboard = false;
if (typeof window !== "undefined") {
  window.addEventListener(
    "keydown",
    (e) => {
      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      if (e.isTrusted && window.innerHeight - viewportHeight < 150)
        hasKeyboard = true;
    },
    { capture: true },
  );
}

type IInputProps = InputHTMLAttributes<HTMLInputElement> & {
  dataGamepad?: string;
};

export interface ITextFieldProps {
  label?: string;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  descriptionMessage?: string;
  inputProps?: IInputProps;
  isShowClear?: boolean;
  upperCase?: boolean;
  maxLength?: number;
  isError?: boolean;
  centerInput?: boolean;
  onChangeValue?: (value: string) => void;
  endAdornment?: React.ReactNode;
  variant?: "outlined" | "standard";
  inputRef?: RefObject<HTMLDivElement>;
  onClick?: () => void;
  showInfoBar?: boolean;
  hideInfoBar?: boolean;
  // When true on TV, opens a custom on-screen keyboard on click instead of
  // the platform info bar. Set `useTVKeyboard={false}` to keep legacy behavior.
  useTVKeyboard?: boolean;
  onTVKeyboardSubmit?: (value: string) => void;
  onTVKeyboardMaxLength?: () => void;
  tvKeyboardPortalTarget?: Element | null;
}

export const TextField: FC<
  ITextFieldProps & HTMLAttributes<HTMLDivElement>
> = ({
  label,
  fullWidth,
  startIcon,
  descriptionMessage,
  isShowClear,
  inputProps,
  maxLength,
  isError,
  onChangeValue,
  endAdornment,
  centerInput,
  upperCase,
  variant = "outlined",
  inputRef,
  onClick,
  hideInfoBar,
  useTVKeyboard = true,
  onTVKeyboardSubmit,
  onTVKeyboardMaxLength,
  tvKeyboardPortalTarget,
  ...otherProps
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRefInput = useRef<HTMLInputElement>(null);
  const keyboardOpenedByTapRef = useRef(false);
  const [value, setValue] = useState<string | number | readonly string[]>(
    inputProps?.value || "",
  );
  const [openRequestClick, setOpenRequestClick] = useState(false);
  const [openRequestRemote, setOpenRequestRemote] = useState(false);
  const [tvKeyboardOpen, setTvKeyboardOpen] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const enableTVKeyboard = useTVKeyboard && isTV;
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const descriptionId = `${fieldId}-description`;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue =
      inputProps?.inputMode === "numeric"
        ? event.target.value.replace(/\D/g, "")
        : event.target.value;
    if (
      maxLength &&
      nextValue.length > value?.toString()?.length &&
      value?.toString()?.length >= maxLength &&
      (event.nativeEvent as InputEvent).inputType !== "deleteContentBackward"
    )
      return;
    onChangeValue?.(nextValue);
    setValue(nextValue);
  };

  const onClear = () => {
    setValue("");
    onChangeValue?.("");
  };

  const onClickInput = (e: React.MouseEvent<HTMLDivElement>) => {
    if (enableTVKeyboard) {
      setTvKeyboardOpen(true);
      // Skip the platform info bar; the on-screen keyboard handles input.
    }

    const isControllerKeyboardTrigger =
      !e.isTrusted && isIOS && !isTV && !hasKeyboard;

    if (isControllerKeyboardTrigger) {
      if (!keyboardOpenedByTapRef.current) {
        setOpenRequestClick(true);
      }
    } else if (onClick) {
      if (e.isTrusted) keyboardOpenedByTapRef.current = true;
      onClick();
    } else if (!enableTVKeyboard) {
      inputRefInput.current?.focus();
      isTV && inputRefInput.current?.click();
    }
  };

  const onTVKeyboardChange = (next: string) => {
    if (inputProps?.inputMode === "numeric") {
      next = next.replace(/\D/g, "");
    }
    if (maxLength && next.length > maxLength) {
      next = next.slice(0, maxLength);
    }
    setValue(next);
    onChangeValue?.(next);
    if (maxLength && next.length === maxLength) {
      closeTVKeyboard();
      onTVKeyboardMaxLength?.();
    }
  };

  const closeTVKeyboard = () => {
    setTvKeyboardOpen(false);
  };

  const onBlurInput = () => {
    setOpenRequestRemote(false);
    keyboardOpenedByTapRef.current = false;
  };

  useEffect(() => {
    setValue(inputProps?.value || "");
  }, [inputProps?.value]);

  return (
    <>
      <Dialog
        open={openRequestClick}
        title={t("GamePlay_Put Aside Your Controller")}
        description={t(
          "TextField_Please tap or click the search box on the screen to continue.",
        )}
        mainAction={{
          id: "close-dialog",
          text: t("TextField_ok"),
          onClick: () => setOpenRequestClick(false),
          "data-gamepad": "default-selected",
        }}
      />
      {enableTVKeyboard && (
        <TVKeyboard
          open={tvKeyboardOpen}
          portalTarget={tvKeyboardPortalTarget}
          value={String(value ?? "")}
          onChange={onTVKeyboardChange}
          replaceOnFirstInput={Boolean(maxLength === 1 && value)}
          onSubmit={(v) => {
            onTVKeyboardSubmit?.(v);
            closeTVKeyboard();
          }}
          onClose={closeTVKeyboard}
          inputRef={inputRefInput}
          onCursorChange={setCursorPos}
        />
      )}
      {openRequestRemote && !hideInfoBar && (
        <div className={classes.infoBar}>
          <Icon name="icon-bold-danger" size={48} />
          <div className={classes.infoBarContent}>
            <Typography variant={TypographyVariant.HeadingH4Bold}>
              {t("GamePlay_Put Aside Your Controller")}
            </Typography>
            <Typography variant={TypographyVariant.BodyB3Regular}>
              {t("GamePlay_Use TV remote control to use the keyboard")}
            </Typography>
          </div>
        </div>
      )}
      {/* Gamepad focus lands on this wrapper, so name it for TV screen readers */}
      <div
        role="group"
        tabIndex={-1}
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : inputProps?.["aria-label"]}
        aria-describedby={descriptionMessage ? descriptionId : undefined}
        {...otherProps}
        className={clsx(
          classes.root,
          fullWidth && classes.fullWidth,
          otherProps.className,
          tvKeyboardOpen && classes.tvKeyboardOpen,
        )}
        data-gamepad={inputProps?.dataGamepad}
        data-selected-class={classes.selected}
        ref={inputRef}
        onClick={onClickInput}
      >
        {label && (
          <Typography
            variant={TypographyVariant.BodyB5Regular}
            color={TypographyColor.Silver}
            data-testid="label-input"
            id={labelId}
            className={classes.label}
          >
            {label}
          </Typography>
        )}
        <div
          className={clsx(classes.content, !fullWidth && classes.fitContent)}
        >
          {startIcon && (
            <div className={classes.startAdornment}>{startIcon}</div>
          )}
          {/* aria-labelledby, not <label htmlFor>: label click would bypass onClickInput */}
          <input
            id={fieldId}
            aria-labelledby={label ? labelId : undefined}
            aria-describedby={descriptionMessage ? descriptionId : undefined}
            aria-invalid={isError || undefined}
            {...inputProps}
            ref={inputRefInput}
            className={clsx(
              classes.input,
              inputProps?.className,
              fullWidth && classes.fullWidth,
              isError && classes.errorBorder,
              inputProps?.disabled && classes.textDisable,
              startIcon && classes.hasStartIcon,
              isShowClear && classes.hasEndIcon,
              centerInput && classes.centerInput,
              upperCase && classes.upperCase,
              enableTVKeyboard && tvKeyboardOpen && classes.inputTextHidden,
              variant,
            )}
            placeholder={inputProps?.placeholder}
            value={value}
            onChange={onChange}
            onKeyDown={inputProps?.onKeyDown}
            onBlur={onBlurInput}
            disabled={inputProps?.disabled}
            readOnly={enableTVKeyboard ? true : inputProps?.readOnly}
          />
          {enableTVKeyboard && tvKeyboardOpen && (
            <div
              className={clsx(
                inputProps?.className,
                classes.cursorOverlay,
                startIcon && classes.cursorOverlayHasStartIcon,
                isShowClear && classes.cursorOverlayHasEndIcon,
                centerInput && classes.cursorOverlayCenter,
              )}
              aria-hidden
            >
              <span
                className={clsx(
                  classes.cursorText,
                  upperCase && classes.upperCase,
                )}
              >
                {String(value ?? "").slice(0, cursorPos)}
              </span>
              <span className={classes.cursorCaret} />
              <span
                className={clsx(
                  classes.cursorText,
                  upperCase && classes.upperCase,
                )}
              >
                {String(value ?? "").slice(cursorPos)}
              </span>
            </div>
          )}
          {isShowClear && value && (
            <StaticImage
              src={CloseIcon}
              src2x={CloseIcon2x}
              src3x={CloseIcon3x}
              className={classes.closeIcon}
              onClick={onClear}
              role="button"
              tabIndex={-1}
              alt={t("A11y_Clear")}
            />
          )}
          {endAdornment && (
            <div className={classes.endAdornment}>{endAdornment}</div>
          )}
        </div>
        {descriptionMessage && (
          <Typography
            variant={TypographyVariant.BodyB6Regular}
            color={isError ? TypographyColor.Error : TypographyColor.Silver}
            data-testid="error-text"
            id={descriptionId}
            role={isError ? "alert" : undefined}
            className={clsx(
              classes.description,
              isError && classes.descriptionError,
            )}
          >
            {isError && (
              <Icon
                size={13}
                name="icon-bold-info-circle"
                className={classes.descriptionErrorIcon}
              />
            )}
            {descriptionMessage}
          </Typography>
        )}
      </div>
    </>
  );
};
